import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./App.css";
import { RouterProvider } from "react-router-dom";
import { areaManagerRouter, loginRouter, adminRouter } from "./routes";
import { styles, theme } from "./styles";
import {
  Box,
  CircularProgress,
  GlobalStyles,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import "./styles/fonts.css";
import { Provider, useDispatch, useSelector } from "react-redux";
import configureStore from "./redux/configureStore";
import StorageService from "./storage";
import { getUserData, logout } from "./redux/user/actions";
import { createLoadingSelector } from "./redux/loading-reducer";
import { UserTypes } from "./redux/user/types";
import { ROLES } from "./constants/roles";
import { appAdminRouter } from "./routes/AppAdminRouter";
import { Worker } from "@react-pdf-viewer/core";
import TyfhuSnack from "./components/TyfhuSnack";
import SessionService from "./services/SessionService";
import ErrorBoundary from "./components/ErrorBoundary";

const Root = () => {
  const user = useSelector((state: any) => state.user.user);
  const dispatch = useDispatch();

  const loadingSelector = createLoadingSelector([UserTypes.GET_USER]);
  const isLoading = useSelector((state) => loadingSelector(state));
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (StorageService.token.getValue()) {
      dispatch(getUserData());
    }
  }, [dispatch]);

  const resetSession = useCallback(async () => {
    setOpen(true);
    StorageService.token.clearAll();
    await dispatch(logout());
  }, [dispatch]);

  useEffect(() => {
    SessionService.observe(() => {
      resetSession();
    });
    return () => {
      SessionService.clear();
    };
  }, [resetSession]);

  const router = useMemo(() => {
    if (user?.role === ROLES.APPADMIN) return appAdminRouter;
    if (user?.role === ROLES.ADMIN) return adminRouter;
    if (user?.role === ROLES.AREAMANAGER) return areaManagerRouter;
    return loginRouter;
  }, [user]);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F8FAFC",
          gap: 2,
        }}
      >
        <CircularProgress color="primary" size={40} thickness={4} />
        <Typography fontSize="14px" color="text.secondary" fontWeight={500}>
          Loading application...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <RouterProvider router={router} />
      <TyfhuSnack
        snackbar={{ message: "Session expired", severity: "error", open }}
        handleClose={() => setOpen(false)}
      />
    </>
  );
};

const Store = configureStore();

function App() {

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <GlobalStyles styles={styles} />
            <Provider store={Store}>
              <Root />
            </Provider>
          </LocalizationProvider>
        </Worker>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
